import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../../../components/ui/Card';
import {
  Building2,
  User,
  MapPin,
  FileText,
  Image,
  Clock,
  ArrowLeft,
  Loader2,
  Download,
  Shield,
} from 'lucide-react';
import {
  useSuperAdminCompanyDetail,
  useSuperAdminCompanyHistory,
} from '../../../hooks/useCompanyRegistration';
import { api } from '../../../lib/api';

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  DRAFT: { label: 'Rascunho', color: 'bg-surface-600 text-surface-300' },
  DOCUMENTS_UPLOADED: { label: 'Documentos Enviados', color: 'bg-blue-500/20 text-blue-400' },
  PENDING_COMPLIANCE: { label: 'Em Análise', color: 'bg-yellow-500/20 text-yellow-400' },
  APPROVED: { label: 'Aprovado', color: 'bg-green-500/20 text-green-400' },
  REJECTED: { label: 'Rejeitado', color: 'bg-red-500/20 text-red-400' },
  PENDING_ADDITIONAL_DOCS: { label: 'Docs Adicionais', color: 'bg-orange-500/20 text-orange-400' },
  ACTIVE: { label: 'Ativo', color: 'bg-emerald-500/20 text-emerald-400' },
};

const DOC_TYPE_LABELS: Record<string, string> = {
  SOCIAL_CONTRACT: 'Contrato Social',
  CNPJ_CARD: 'Cartão CNPJ',
  DOC_FRONT: 'Documento — Frente',
  DOC_BACK: 'Documento — Verso',
  SELFIE_WITH_DOC: 'Selfie com Documento',
  DOC_PDF: 'PDF do Documento',
  ADDITIONAL: 'Documento Adicional',
};

function formatCnpj(cnpj: string) {
  const d = cnpj?.replace(/\D/g, '') || '';
  return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

function maskCpf(cpf: string) {
  const d = cpf?.replace(/\D/g, '') || '';
  return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

function formatDate(dateStr?: string) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export function CompanyDetailAll() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: company, isLoading } = useSuperAdminCompanyDetail(id || '');
  const { data: history } = useSuperAdminCompanyHistory(id || '');

  const handleDownload = async (docId: string, fileName: string) => {
    try {
      const response = await api.get(`/super-admin/companies/${id}/documents/${docId}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // silently fail
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-primary-400" />
      </div>
    );
  }

  if (!company) {
    return <div className="text-center py-20 text-surface-400">Empresa não encontrada.</div>;
  }

  const statusInfo = STATUS_LABELS[company.status] || { label: company.status, color: 'bg-surface-700 text-surface-300' };
  const timelineHistory = history || company.history || [];

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/super-admin/companies')} className="text-surface-400 hover:text-white">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">{company.companyName}</h1>
            <p className="text-surface-400 font-mono text-sm">{formatCnpj(company.companyCnpj)}</p>
          </div>
        </div>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color}`}>
          {statusInfo.label}
        </span>
      </div>

      {/* Tenant Info */}
      {company.tenant && (
        <Card>
          <div className="p-4 flex items-center gap-3">
            <Shield size={18} className="text-yellow-400" />
            <div>
              <p className="text-sm text-surface-300">Tenant: <span className="text-white font-medium">{company.tenant.name}</span></p>
              <p className="text-xs text-surface-500 font-mono">{company.tenantId}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Company Data */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Building2 size={18} className="text-primary-400" /> Dados da Empresa
            </h2>
            <div className="space-y-2 text-sm">
              <Row label="Nome" value={company.companyName} />
              <Row label="CNPJ" value={formatCnpj(company.companyCnpj)} />
              <Row label="Tipo" value={company.companyType} />
              <Row label="Nome Fantasia" value={company.tradeName} />
              <Row label="Email" value={company.companyEmail} />
              <Row label="Telefone" value={company.companyPhone} />
              {company.companyWebsite && <Row label="Website" value={company.companyWebsite} />}
              <Row label="Descrição" value={company.companyDescription} />
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <User size={18} className="text-primary-400" /> Representante Legal
            </h2>
            <div className="space-y-2 text-sm">
              <Row label="Nome" value={company.representativeName} />
              <Row label="CPF" value={maskCpf(company.representativeCpf)} />
              <Row label="Documento" value={company.representativeDocType} />
              <Row label="Nascimento" value={company.representativeBirthDate} />
              <Row label="Telefone" value={company.representativePhone} />
              <Row label="Nome da Mãe" value={company.representativeMotherName} />
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <MapPin size={18} className="text-primary-400" /> Endereço
            </h2>
            <div className="space-y-2 text-sm">
              <Row label="CEP" value={company.addressZipCode} />
              <Row label="Logradouro" value={`${company.addressStreet}, ${company.addressNumber}`} />
              <Row label="Bairro" value={company.addressNeighborhood} />
              <Row label="Cidade/UF" value={`${company.addressCity} - ${company.addressState}`} />
              {company.addressComplement && <Row label="Complemento" value={company.addressComplement} />}
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Clock size={18} className="text-primary-400" /> Informações
            </h2>
            <div className="space-y-2 text-sm">
              <Row label="Email da Conta" value={company.accountEmail} />
              <Row label="Cadastrado em" value={formatDate(company.createdAt)} />
              {company.submittedAt && <Row label="Enviado em" value={formatDate(company.submittedAt)} />}
              {company.approvedAt && <Row label="Aprovado em" value={formatDate(company.approvedAt)} />}
              {company.rejectedAt && <Row label="Rejeitado em" value={formatDate(company.rejectedAt)} />}
              {company.rejectionReason && <Row label="Motivo Rejeição" value={company.rejectionReason} />}
              {company.gatewayCompanyId && <Row label="ID Gateway" value={company.gatewayCompanyId} />}
              <Row label="Reenvios" value={String(company.resubmissionCount)} />
            </div>
          </div>
        </Card>
      </div>

      {/* Documents with Download */}
      {company.documents && company.documents.length > 0 && (
        <Card>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <FileText size={18} className="text-primary-400" /> Documentos
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {company.documents.filter(d => !d.isReplaced).map(doc => (
                <div key={doc.id} className="p-3 rounded-lg bg-surface-800 space-y-2">
                  <div className="flex items-center gap-2">
                    {doc.fileMimeType?.startsWith('image/') ? (
                      <Image size={16} className="text-blue-400" />
                    ) : (
                      <FileText size={16} className="text-red-400" />
                    )}
                    <span className="text-sm text-white font-medium">
                      {DOC_TYPE_LABELS[doc.documentType] || doc.documentType}
                    </span>
                  </div>
                  <p className="text-xs text-surface-400 truncate">{doc.fileName}</p>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-surface-500">{(doc.fileSizeBytes / 1024).toFixed(0)} KB</p>
                    <button onClick={() => handleDownload(doc.id, doc.fileName)}
                      className="text-primary-400 hover:text-primary-300 transition-colors">
                      <Download size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Complete Timeline */}
      {timelineHistory.length > 0 && (
        <Card>
          <div className="p-6 space-y-4">
            <h2 className="text-lg font-semibold text-white">Histórico Completo</h2>
            <div className="space-y-3">
              {timelineHistory.map(entry => (
                <div key={entry.id} className="flex items-start gap-3 text-sm">
                  <div className="w-2 h-2 rounded-full bg-primary-500 mt-1.5 flex-shrink-0" />
                  <div>
                    <p className="text-surface-300">
                      {entry.action.replace(/_/g, ' ')}
                      {entry.fromStatus && entry.toStatus && (
                        <span className="text-surface-500">
                          {' '}({STATUS_LABELS[entry.fromStatus]?.label || entry.fromStatus} → {STATUS_LABELS[entry.toStatus]?.label || entry.toStatus})
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-surface-500">{formatDate(entry.changedAt)}</p>
                    {entry.notes && <p className="text-xs text-surface-400 mt-0.5">{entry.notes}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-surface-400">{label}</span>
      <span className="text-white text-right max-w-[60%] break-words">{value || '—'}</span>
    </div>
  );
}
